/**
 * シフト表(月間。2026-09-12)。現行「【日報2609】コールセンター」のシフト表タブと同じ見た目。
 *  - 縦 = スタッフ / 横 = 日付。セルは "9-18" のような時間、入らない日は空
 *  - **提出と確定は分けない**。提出されたものをそのまま出し、欠勤などで変わったら管理者がそのセルを直す
 *  - 上に 稼働合計 と 想定件数(稼働 × 2.0件/h)、絞り込みは 全体 / 品川 / 在宅
 *  - **管理(出勤形態=管理)は一番上に固定**
 * 日付の見出しをクリックするとその日の出勤表へ移動する。
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { dayColorOf } from '@core/lib/holidays';
import type { DateStr, MonthStr, Operator, WorkStyle } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { fetchOperators } from '@core/masters/operatorRepo';
import { apphOf, costRate, staffMetrics, sumAgg } from '@core/dashboard/calc';
import { fetchMonthTargets, fetchSummariesRange } from '@core/dashboard/dashboardRepo';
import { addMonths, daysInMonth, fmtNum, fmtPct } from '@core/dashboard/calc';
import { TARGET_PER_HOUR, TARGET_PER_HOUR_BY_BUSINESS, businessOfBbId } from './shiftRepo';
import Modal from '@core/components/Modal';
import { dayHours, fetchSubmissions, saveSubmission, type ShiftChangeLog, type ShiftSubmission, type SubmitDay } from './shiftSubmitRepo';

type Scope = 'all' | 'shinagawa' | 'zaitaku';
const SCOPES: { key: Scope; label: string }[] = [
  { key: 'all', label: '全体' },
  { key: 'shinagawa', label: '出勤' },
  { key: 'zaitaku', label: '在宅' },
];
/** 出勤形態×報酬区分のまとまり(現行シートの並び)。管理が先頭 */
type GroupKey = 'kanri' | 'shinagawa' | 'timee' | 'zaitaku_jikyu' | 'zaitaku_buai';
const GROUPS: { key: GroupKey; label: string; cls: string; sums: ('hours' | 'rate' | 'ex_timee' | 'people')[] }[] = [
  { key: 'kanri', label: '管理', cls: 'g-kanri', sums: ['hours'] },
  { key: 'shinagawa', label: '出勤', cls: 'g-shinagawa', sums: ['hours', 'rate', 'ex_timee'] },
  { key: 'timee', label: '単発', cls: 'g-timee', sums: ['hours', 'people'] },
  { key: 'zaitaku_jikyu', label: '在宅／時給', cls: 'g-jikyu', sums: ['hours', 'people'] },
  { key: 'zaitaku_buai', label: '在宅／歩合', cls: 'g-buai', sums: ['hours', 'people'] },
];
const SUM_LABELS = { hours: '稼働時間合計', rate: '稼働率', ex_timee: 'タイミー抜き', people: '稼働人数合計' } as const;

const cellText = (d: SubmitDay | undefined) => (!d ? '' : d.status === 'absent' ? '欠勤' : d.status === 'same_day' ? '当欠' : `${d.start}-${d.end}`);
const hours = Array.from({ length: 23 }, (_, i) => 8 + i * 0.5);
const hhmm = (h: number) => `${Math.floor(h)}:${h % 1 ? '30' : '00'}`;

export default function ShiftMonthTab({ onOpenDate }: { onOpenDate: (d: DateStr) => void }) {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [month, setMonth] = useState<MonthStr>(todayStr().slice(0, 7) as MonthStr);
  const [scope, setScope] = useState<Scope>('all');
  const [subs, setSubs] = useState<ShiftSubmission[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  /** op_id → 今月の実績(原価率・取得成績)。日次【集計】と同じ計算 */
  const [perf, setPerf] = useState<Map<string, { costRate: number | null; apph: number | null }>>(new Map());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = `${month}-01` as DateStr;
      const to = `${month}-${String(daysInMonth(month)).padStart(2, '0')}` as DateStr;
      const [s, ops, sums, tg, cs] = await Promise.all([
        fetchSubmissions(month),
        isAdmin ? fetchOperators() : Promise.resolve([] as Operator[]),
        fetchSummariesRange(from, to),
        fetchMonthTargets(month),
        fetchCompanies(),
      ]);
      setSubs(s);
      setOperators(ops);
      const cmap = new Map(cs.map((c) => [c.id, c]));
      const metrics = sums.flatMap((x) => x.staff.map((st) => staffMetrics(st, x, tg, cmap)));
      const m = new Map<string, { costRate: number | null; apph: number | null }>();
      for (const opId of new Set(metrics.map((x) => x.op_id))) {
        const agg = sumAgg(metrics.filter((x) => x.op_id === opId));
        m.set(opId, { costRate: costRate(agg), apph: apphOf(agg) });
      }
      setPerf(m);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [month, isAdmin, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const days = useMemo(() => Array.from({ length: daysInMonth(month) }, (_, i) => String(i + 1).padStart(2, '0')), [month]);
  const opOf = useCallback((opId: string) => operators.find((o) => o.id === opId) ?? null, [operators]);
  const wsOf = useCallback((opId: string): WorkStyle => opOf(opId)?.work_style ?? 'zaitaku', [opOf]);
  /** その人のまとまり(出勤形態×報酬区分) */
  const groupOf = useCallback(
    (opId: string): GroupKey => {
      const op = opOf(opId);
      const ws = op?.work_style ?? 'zaitaku';
      if (ws === 'kanri') return 'kanri';
      if (ws === 'shinagawa') return 'shinagawa';
      if (ws === 'timee') return 'timee';
      return op?.pay_type === 'commission' ? 'zaitaku_buai' : 'zaitaku_jikyu';
    },
    [opOf],
  );
  /** 報酬(時給 or 件数単価)。先頭のBB IDの現在値 */
  const wageOf = useCallback((opId: string) => opOf(opId)?.bb_accounts[0]?.current_wage ?? null, [opOf]);

  const rows = useMemo(() => {
    const list = subs
      .filter((s) => (isAdmin ? true : s.op_id === user?.op_id))
      .filter((s) => {
        if (scope === 'all') return true;
        const ws = wsOf(s.op_id);
        if (ws === 'kanri') return true; // 管理は常に出す
        return scope === 'shinagawa' ? ws === 'shinagawa' || ws === 'timee' : ws === 'zaitaku';
      });
    return [...list].sort((a, b) => {
      const d = GROUPS.findIndex((g) => g.key === groupOf(a.op_id)) - GROUPS.findIndex((g) => g.key === groupOf(b.op_id));
      return d !== 0 ? d : a.name.localeCompare(b.name, 'ja');
    });
  }, [subs, isAdmin, user?.op_id, scope, wsOf, groupOf]);

  /** まとまりの開閉(全体表示のとき、出勤形態×報酬で表示/非表示できる) */
  const [closed, setClosed] = useState<Set<GroupKey>>(new Set());
  const toggleGroup = (k: GroupKey) =>
    setClosed((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const hoursOf = (s: ShiftSubmission, d: string) => dayHours(s.days[d]);
  const countStatus = (s: ShiftSubmission, st: 'absent' | 'same_day') => days.filter((d) => s.days[d]?.status === st).length;
  const dayTotal = (d: string) => rows.reduce((a, s) => a + hoursOf(s, d), 0);
  const rowTotal = (s: ShiftSubmission) => days.reduce((a, d) => a + hoursOf(s, d), 0);
  const grand = rows.reduce((a, s) => a + rowTotal(s), 0);
  /** 想定件数 = 稼働 × 想定取得成績。その日のBB IDの架電内容で係数を変える(給湯器 2.0件/h・不用品 3時間1件) */
  const dayTarget = (d: string) =>
    rows.reduce((a, s) => a + hoursOf(s, d) * TARGET_PER_HOUR_BY_BUSINESS[businessOfBbId(operators, s.days[d]?.bb_id || s.bb_id)], 0);
  const grandTarget = days.reduce((a, d) => a + dayTarget(d), 0);

  /** 編集中のセル */
  const [edit, setEdit] = useState<{ s: ShiftSubmission; day: string; start: number | null; end: number | null } | null>(null);
  const [saving, setSaving] = useState(false);

  const openCell = (s: ShiftSubmission, day: string) => {
    const v = s.days[day];
    setEdit({ s, day, start: v?.start ?? 9, end: v?.end ?? 18 });
  };

  const applyCell = async (remove: boolean, status: '' | 'absent' | 'same_day') => {
    if (!edit || !user) return;
    const { s, day } = edit;
    const before = cellText(s.days[day]) || '休み';
    const next = { ...s.days };
    if (remove) delete next[day];
    else {
      if (edit.start === null || edit.end === null || edit.end <= edit.start) {
        toast('終了は開始より後にしてください');
        return;
      }
      const d: SubmitDay = { bb_id: s.days[day]?.bb_id ?? s.bb_id, start: edit.start, end: edit.end };
      if (status) d.status = status;
      next[day] = d;
    }
    const after = cellText(next[day]) || '休み';
    if (before === after) {
      setEdit(null);
      return;
    }
    const log: ShiftChangeLog = { day, before, after, by: user.name || user.email || user.id, at: new Date().toISOString() };
    const logs = [log, ...(s.logs ?? [])];
    setSaving(true);
    try {
      await saveSubmission({ month, opId: s.op_id, name: s.name, bbId: s.bb_id, days: next, note: s.note ?? '', userId: user.id, logs });
      setSubs((prev) => prev.map((x) => (x.id === s.id ? { ...x, days: next, logs } : x)));
      setEdit(null);
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  /** 変更があった日(一覧で印を付ける) */
  const changedDays = useCallback((s: ShiftSubmission) => new Set((s.logs ?? []).map((l) => l.day)), []);

  return (
    <div className="card">
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        シフト表
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <button className="txn-link" onClick={() => setMonth(addMonths(month, -1) as MonthStr)}>前の月</button>
          <b>{month.slice(0, 4)}年{Number(month.slice(5))}月</b>
          <button className="txn-link" onClick={() => setMonth(addMonths(month, 1) as MonthStr)}>次の月</button>
        </span>
        <span className="chips" style={{ marginBottom: 0 }}>
          {SCOPES.map((s) => (
            <button key={s.key} className={`chip${scope === s.key ? ' on' : ''}`} onClick={() => setScope(s.key)}>
              {s.label}
            </button>
          ))}
        </span>
        {isAdmin && <span className="hint" style={{ fontWeight: 400 }}>セルをクリックすると直せます(例 9-18／休みは空欄)</span>}
      </h3>

      {loading ? (
        <div className="empty">読み込み中…</div>
      ) : rows.length === 0 ? (
        <div className="empty">この月の提出がまだありません</div>
      ) : (
        <div className="table-scroll">
          <table className="fit-table shift-month">
            <thead>
              <tr>
                <th style={{ width: 104 }}>名前</th>
                <th className="ws-cell" style={{ width: 46 }}>形態</th>
                <th className="num" style={{ width: 54 }}>原価率</th>
                <th className="num" style={{ width: 54 }}>取得成績</th>
                <th className="num" style={{ width: 58 }}>報酬額</th>
                <th className="num" style={{ width: 40 }}>欠勤</th>
                <th className="num" style={{ width: 40 }}>当欠</th>
                {days.map((d) => {
                  const date = `${month}-${d}` as DateStr;
                  const c = dayColorOf(date);
                  return (
                    <th key={d} className={`num day ${c === 'red' ? 'sun' : c === 'blue' ? 'sat' : ''}`}>
                      <button className="txn-link" onClick={() => onOpenDate(date)}>
                        {Number(d)} {weekdayLabel(date).replace(/[()]/g, '')}
                      </button>
                    </th>
                  );
                })}
                <th className="num" style={{ width: 42 }}>日数</th>
                <th className="num" style={{ width: 54 }}>合計</th>
              </tr>
              <tr className="shift-sum">
                <th colSpan={7}>稼働合計</th>
                {days.map((d) => (
                  <th key={d} className="num day">{dayTotal(d) ? fmtNum(dayTotal(d), 1) : ''}</th>
                ))}
                <th className="num">{rows.filter((s) => rowTotal(s) > 0).length}</th>
                <th className="num">{fmtNum(grand, 1)}</th>
              </tr>
              <tr className="shift-sum">
                <th colSpan={7}>想定件数(×{TARGET_PER_HOUR}・不用品は3時間1件)</th>
                {days.map((d) => (
                  <th key={d} className="num day">{dayTotal(d) ? Math.round(dayTarget(d)) : ''}</th>
                ))}
                <th />
                <th className="num">{Math.round(grandTarget)}</th>
              </tr>
            </thead>
            <tbody>
              {GROUPS.map((g) => {
                const list = rows.filter((s) => groupOf(s.op_id) === g.key);
                if (list.length === 0) return null;
                const open = !closed.has(g.key);
                const gHours = (d: string) => list.reduce((a, s) => a + hoursOf(s, d), 0);
                const gPeople = (d: string) => list.filter((s) => hoursOf(s, d) > 0).length;
                const exTimee = (d: string) => list.filter((s) => wsOf(s.op_id) !== 'timee').reduce((a, s) => a + hoursOf(s, d), 0);
                return (
                  <Fragment key={g.key}>
                    <tr className={`shift-grp ${g.cls}`}>
                      <th colSpan={7}>
                        <button className="grp-toggle" onClick={() => toggleGroup(g.key)}>
                          {open ? '−' : '＋'} {g.label}({list.length}名)
                        </button>
                      </th>
                      {days.map((d) => (
                        <th key={d} className="num day" />
                      ))}
                      <th />
                      <th />
                    </tr>
                    {g.sums.map((k) => (
                      <tr key={k} className="shift-gsum">
                        <th colSpan={7}>{SUM_LABELS[k]}</th>
                        {days.map((d) => {
                          const v = k === 'hours' ? gHours(d) : k === 'people' ? gPeople(d) : k === 'ex_timee' ? exTimee(d) : null;
                          return (
                            <td key={d} className="num day">
                              {k === 'rate' ? '—' : v ? (k === 'people' ? v : fmtNum(v, 1)) : ''}
                            </td>
                          );
                        })}
                        <td />
                        <td className="num">
                          {k === 'hours' ? fmtNum(list.reduce((a, s) => a + rowTotal(s), 0), 1) : k === 'people' ? list.filter((s) => rowTotal(s) > 0).length : ''}
                        </td>
                      </tr>
                    ))}
                    {open &&
                      list.map((s) => (
                        <tr key={s.id} className={g.key === 'kanri' ? 'is-kanri' : undefined}>
                          <td>{s.name}</td>
                          <td className="ws-cell">
                            <span className={`ws-tag ${g.cls}`}>{g.label}</span>
                          </td>
                          <td className="num">{fmtPct(perf.get(s.op_id)?.costRate ?? null)}</td>
                          <td className="num">{fmtNum(perf.get(s.op_id)?.apph ?? null, 2)}</td>
                          <td className="num">{wageOf(s.op_id) === null ? '' : `¥${(wageOf(s.op_id) ?? 0).toLocaleString('ja-JP')}`}</td>
                          <td className="num">{countStatus(s, 'absent') || ''}</td>
                          <td className="num">{countStatus(s, 'same_day') || ''}</td>
                          {days.map((d) => {
                            const v = s.days[d];
                            const changed = changedDays(s).has(d);
                            return (
                              <td
                                key={d}
                                className={`day${v ? ' has' : ''}${v?.status ? ' off' : ''}${changed ? ' changed' : ''}${isAdmin ? ' clickable' : ''}`}
                                title={changed ? (s.logs ?? []).filter((l) => l.day === d).map((l) => `${l.before} → ${l.after}(${l.by})`).join(' / ') : undefined}
                                onClick={isAdmin ? () => openCell(s, d) : undefined}
                              >
                                {cellText(v)}
                              </td>
                            );
                          })}
                          <td className="num">{days.filter((d) => hoursOf(s, d) > 0).length}</td>
                          <td className="num"><b>{fmtNum(rowTotal(s), 1)}</b></td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <Modal onClose={() => setEdit(null)} width="min(420px, 94vw)">
          <h3>
            {edit.s.name}・{Number(edit.day)}日{weekdayLabel(`${month}-${edit.day}` as DateStr)}
          </h3>
          <div className="shift-edit">
            <label>開始</label>
            <select value={edit.start ?? ''} onChange={(e) => setEdit({ ...edit, start: Number(e.target.value) })}>
              {hours.map((h) => (
                <option key={h} value={h}>
                  {hhmm(h)}
                </option>
              ))}
            </select>
            <label>終了</label>
            <select value={edit.end ?? ''} onChange={(e) => setEdit({ ...edit, end: Number(e.target.value) })}>
              {hours.map((h) => (
                <option key={h} value={h}>
                  {hhmm(h)}
                </option>
              ))}
            </select>
          </div>
          {(edit.s.logs ?? []).filter((l) => l.day === edit.day).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="hint" style={{ marginBottom: 4 }}>この日の変更履歴</div>
              <ul style={{ fontSize: 12, paddingLeft: 16 }}>
                {(edit.s.logs ?? [])
                  .filter((l) => l.day === edit.day)
                  .slice(0, 5)
                  .map((l, i) => (
                    <li key={i}>
                      {l.before} → {l.after}({l.by}・{l.at.slice(0, 16).replace('T', ' ')})
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <div className="modal-actions center">
            <button className="btn-line" disabled={saving} onClick={() => void applyCell(false, 'absent')}>
              欠勤
            </button>
            <button className="btn-line" disabled={saving} onClick={() => void applyCell(false, 'same_day')}>
              当欠
            </button>
            <button className="btn" disabled={saving} onClick={() => void applyCell(false, '')}>
              {saving ? '保存中…' : '保存'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
